import type { CSSProperties, ReactNode } from "react";

import { sx } from "../style";

export type BadgeTone = "neutral" | "brand" | "teal" | "success" | "warning" | "error" | "info";

const tones: Record<BadgeTone, [string, string, string]> = {
  neutral: ["var(--neutral-100)", "var(--neutral-600)", "var(--neutral-200)"],
  brand: ["var(--blue-50)", "var(--blue-600)", "var(--blue-100)"],
  teal: ["var(--teal-50)", "var(--teal-700)", "var(--teal-100)"],
  success: ["var(--color-status-success-bg)", "var(--green-700)", "var(--color-status-success-border)"],
  warning: ["var(--color-status-warning-bg)", "var(--amber-700)", "var(--color-status-warning-border)"],
  error: ["var(--color-status-error-bg)", "var(--red-700)", "var(--color-status-error-border)"],
  info: ["var(--color-status-info-bg)", "var(--infoblue-600)", "var(--color-status-info-border)"],
};

export interface BadgeProps {
  children?: ReactNode;
  tone?: BadgeTone;
  dot?: boolean;
  outline?: boolean;
  style?: CSSProperties;
}

/** Small count/label pill. For document lifecycle use StatusChip instead. */
export function Badge({ children, tone = "neutral", dot = false, outline = false, style }: BadgeProps) {
  const [bg, fg, border] = tones[tone] || tones.neutral;
  return (
    <span
      style={sx({
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 8px",
        borderRadius: "var(--radius-pill)",
        background: outline ? "transparent" : bg,
        color: fg,
        border: outline ? `1px solid ${border}` : "1px solid transparent",
        fontSize: "var(--fs-badge)",
        lineHeight: "var(--lh-badge)",
        fontWeight: "var(--fw-semibold)",
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        ...style,
      })}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: fg }} />}
      {children}
    </span>
  );
}
